<script lang="ts" setup>
import type { DriverDto, DriverMobileDevice } from "@/api/users/dto/driver.dto";
import { computed } from "vue";
import dayjs from "dayjs";
import TrackingStatus from "@/views/users/components/TrackingStatus.vue";
import OnlineStatus from "@/views/users/components/OnlineStatus.vue";
import DeviceInfo from "@/views/users/components/DeviceInfo.vue";
import { useAppStore } from "@/store/app.store";
import { useFilters } from "@/composables/useFilters";
import { useDateTime } from "@/composables";

const props = defineProps<{
  user: DriverDto;
}>();

const appStore = useAppStore();
const { date } = useFilters();
const { timeAgo } = useDateTime();

const hiredByName = computed(() => {
  const hiredBy = appStore.dispatchers.find((d) => d.id === props.user.hiredById);
  return hiredBy ? hiredBy.name : null;
});

const reversedDevices = computed<DriverMobileDevice[]>(() => {
  if (!props.user?.mobileUserDevices) {
    return [];
  }

  return [...props.user.mobileUserDevices].reverse();
});

const createdDate = computed(() => date(props.user.createdTime || dayjs().toISOString()));
</script>

<template>
  <div class="user-block pt-5 pr-5 pb-7 pl-5 bg-white rounded mt-4">
    <div class="text-grey-darken-3 mb-6 font-weight-medium">User info</div>
    <v-row>
      <div class="label">
        <span class="text-grey-darken-1">GPS Tracking</span>
      </div>
      <div class="description">
        <tracking-status :user="user"></tracking-status>
      </div>
    </v-row>
    <v-row align="center">
      <div class="label">
        <span class="text-grey-darken-1">Last online:</span>
      </div>
      <div class="description">
        <online-status :user="user"></online-status>
      </div>
    </v-row>
    <v-row>
      <div class="label">
        <span class="text-grey-darken-1">Device(s) info:</span>
      </div>
      <div class="description">
        <device-info v-if="reversedDevices.length" :devices="reversedDevices"></device-info>
        <div v-else>&mdash;</div>
      </div>
    </v-row>
    <v-row>
      <div class="label">
        <span class="text-grey-darken-1">Hired by:</span>
      </div>
      <div class="description">
        <router-link class="text-primary text-decoration-none" to="">{{ hiredByName }}</router-link>
      </div>
    </v-row>
    <v-row>
      <div class="label">
        <span class="text-grey-darken-1">Created date:</span>
      </div>
      <div class="description">
        {{ createdDate }}
      </div>
    </v-row>
  </div>
</template>

<style lang="scss" scoped></style>
